// ────────────────────────────────────────────────────────────────────────────
// TableOpsAdmin — Admin-level TableOps management split into four sub‑views:
//   1. Map Editor      — design floor-plan layouts
//   2. Entities        — browse / delete registered scope entities (cascade)
//   3. Menu Import     — import carta → section → item hierarchy into scopes
//   4. Register PM Link — link / unlink payment methods to registers
//
// Lives under Admin Dashboard → "Table Ops" tab.
// ────────────────────────────────────────────────────────────────────────────
import React, { lazy, Suspense, useState, useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PenTool, Database, UtensilsCrossed, Loader2, Link2, BarChart3, BookOpen } from 'lucide-react'
import { useRbac } from '@/shared/auth/useRbac'

const MapEditorView = lazy(
  () => import('@/features/system/developer/components/simulations/table-ops/editor/MapEditor'),
)
const EntitySimView = lazy(
  () => import('@/features/system/developer/components/simulations/table-ops/EntitySimulator'),
)
const MenuImporterView = lazy(
  () => import('@/features/system/admin/components/MenuImporter'),
)
const MenuManagerView = lazy(
  () => import('@/features/system/admin/components/MenuManagerAdmin'),
)
const RegisterPmLinkView = lazy(
  () => import('@/features/system/admin/components/RegisterPmLink'),
)
const ShiftsView = lazy(
  () => import('@/features/tableops/shifts/components/ShiftsPage'),
)

type SubView = 'editor' | 'entities' | 'manager' | 'menu' | 'pmlink' | 'shifts'

const fallback = (
  <div className="flex items-center justify-center h-40 text-neutral-600 text-sm gap-2">
    <Loader2 size={14} className="animate-spin" /> Loading…
  </div>
)

const VALID_VIEWS = new Set<SubView>(['editor', 'entities', 'manager', 'menu', 'pmlink', 'shifts']);

export default function TableOpsAdmin() {
  const { can } = useRbac();
  const canManageMenu = can('canAdminTableOps');
  // Read initial sub-view from URL (enables BubbleNav deep-linking on mobile)
  const [sp] = useSearchParams();
  const [view, setView] = useState<SubView>(() => {
    const v = sp.get('toView') as SubView | null;
    return v && VALID_VIEWS.has(v) ? v : 'entities';
  })

  // Sync with URL when BubbleNav navigates while already mounted
  useEffect(() => {
    const v = sp.get('toView') as SubView | null;
    if (v && VALID_VIEWS.has(v) && v !== view) setView(v);
  }, [sp]);

  const btn = useCallback(
    (v: SubView, label: string, Icon: React.ElementType) => (
      <button
        onClick={() => setView(v)}
        className={`flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-lg transition-all
          ${view === v
            ? 'bg-white text-black'
            : 'pac-glass-btn text-neutral-500 hover:text-neutral-300 hover:border-neutral-600'
          }`}
      >
        <Icon size={12} />
        {label}
      </button>
    ),
    [view],
  )

  return (
    <div className="flex flex-col h-full min-h-0 bg-black rounded-lg overflow-hidden border border-neutral-800">
      {/* ── sub‑nav — desktop only; BubbleNav handles mobile ── */}
      <header className="shrink-0 hidden md:flex items-center gap-2 px-4 py-2 border-b border-white/5 pac-glass-subtle">
        {btn('editor', 'Map Editor', PenTool)}
        {btn('entities', 'Entities', Database)}
        {canManageMenu && btn('manager', 'Menu', BookOpen)}
        {btn('menu', 'Menu Import', UtensilsCrossed)}
        {btn('pmlink', 'PM Link', Link2)}
        {btn('shifts', 'Shifts', BarChart3)}
      </header>

      {/* ── content ── */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <Suspense fallback={fallback}>
          {view === 'editor' && <MapEditorView />}
          {view === 'entities' && <EntitySimView />}
          {view === 'manager' && <MenuManagerView />}
          {view === 'menu' && <MenuImporterView />}
          {view === 'pmlink' && <RegisterPmLinkView />}
          {view === 'shifts' && <ShiftsView />}
        </Suspense>
      </div>
    </div>
  )
}
